/**
 * Recoloração de outfits Myst/Tibia (head / body / legs / feet).
 * Camada 0 = base; camada 1 = máscara RGBY (amarelo/vermelho/verde/azul).
 * Pele e contorno nunca recebem tint (protege o tom de pele do sprite).
 * Máscaras podem ser limpas com tools/refine-look-masks.js.
 */

export const HSI_H_STEPS = 18;
export const HSI_SI_VALUES = 7;
export const OUTFIT_COLOR_COUNT = HSI_H_STEPS * HSI_SI_VALUES; // 133 (0–132)

export const OUTFIT_PIECES = Object.freeze(['head', 'body', 'legs', 'feet']);

export const DEFAULT_OUTFIT_COLORS = Object.freeze({
  head: 0,
  body: 0,
  legs: 0,
  feet: 0,
});

/** Tabela HSI do OTClient (Outfit::getColor). */
export function getOutfitColor(colorId) {
  let color = Math.max(0, Math.min(OUTFIT_COLOR_COUNT - 1, colorId | 0));
  let loc1 = 0;
  let loc2 = 0;
  let loc3 = 0;
  if (color % HSI_H_STEPS !== 0) {
    loc1 = (color % HSI_H_STEPS) * (1 / 18);
    loc2 = 1;
    loc3 = 1;
    switch (Math.floor(color / HSI_H_STEPS)) {
      case 0: loc2 = 0.25; loc3 = 1; break;
      case 1: loc2 = 0.25; loc3 = 0.75; break;
      case 2: loc2 = 0.5; loc3 = 0.75; break;
      case 3: loc2 = 0.667; loc3 = 0.75; break;
      case 4: loc2 = 1; loc3 = 1; break;
      case 5: loc2 = 1; loc3 = 0.75; break;
      case 6: loc2 = 1; loc3 = 0.5; break;
      default: break;
    }
  } else {
    loc1 = 0;
    loc2 = 0;
    loc3 = 1 - color / HSI_H_STEPS / HSI_SI_VALUES;
  }
  if (loc3 === 0) return { r: 0, g: 0, b: 0 };
  if (loc2 === 0) {
    const gray = Math.round(loc3 * 255);
    return { r: gray, g: gray, b: gray };
  }
  let red = 0;
  let green = 0;
  let blue = 0;
  if (loc1 < 1 / 6) {
    red = loc3;
    blue = loc3 * (1 - loc2);
    green = blue + (loc3 - blue) * 6 * loc1;
  } else if (loc1 < 2 / 6) {
    green = loc3;
    blue = loc3 * (1 - loc2);
    red = green - (loc3 - blue) * (6 * loc1 - 1);
  } else if (loc1 < 3 / 6) {
    green = loc3;
    red = loc3 * (1 - loc2);
    blue = red + (loc3 - red) * (6 * loc1 - 2);
  } else if (loc1 < 4 / 6) {
    blue = loc3;
    red = loc3 * (1 - loc2);
    green = blue - (loc3 - red) * (6 * loc1 - 3);
  } else if (loc1 < 5 / 6) {
    blue = loc3;
    green = loc3 * (1 - loc2);
    red = green + (loc3 - green) * (6 * loc1 - 4);
  } else {
    red = loc3;
    green = loc3 * (1 - loc2);
    blue = red - (loc3 - green) * (6 * loc1 - 5);
  }
  return {
    r: Math.round(red * 255),
    g: Math.round(green * 255),
    b: Math.round(blue * 255),
  };
}

export function outfitColorCss(colorId) {
  const { r, g, b } = getOutfitColor(colorId);
  return `rgb(${r},${g},${b})`;
}

export function normalizeOutfitColors(input) {
  const src = input && typeof input === 'object' ? input : {};
  const out = {};
  for (const piece of OUTFIT_PIECES) {
    const value = Number(src[piece]);
    out[piece] = Number.isFinite(value)
      ? Math.max(0, Math.min(OUTFIT_COLOR_COUNT - 1, Math.round(value)))
      : DEFAULT_OUTFIT_COLORS[piece];
  }
  return out;
}

export function outfitColorsKey(colors) {
  const c = normalizeOutfitColors(colors);
  return `${c.head}-${c.body}-${c.legs}-${c.feet}`;
}

export function entityMaskAsset(name, dir = 'south', frame = 0) {
  const slug = String(name || '').toLowerCase().replace(/_/g, '-');
  return `public/assets/entities/${encodeURIComponent(slug)}-${dir}-${frame}-mask.png`;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load ${src}`));
    img.src = src;
  });
}

/** Classifica pixel da máscara: yellow=head, red=body, green=legs, blue=feet. */
function maskPiece(r, g, b, a) {
  if (a < 16) return null;
  const yellow = Math.min(r, g);
  if (yellow >= 80 && b <= yellow * 0.55) return 'head';
  if (r >= 80 && r >= g * 1.4 && r >= b * 1.4) return 'body';
  if (g >= 80 && g >= r * 1.4 && g >= b * 1.4) return 'legs';
  if (b >= 80 && b >= r * 1.4 && b >= g * 1.4) return 'feet';
  const max = Math.max(r, g, b);
  if (max < 40) return null;
  if (r === max && g === max) return 'head';
  if (r === max) return 'body';
  if (g === max) return 'legs';
  return 'feet';
}

/** Pele típica dos sprites Myst — nunca deve receber tint de roupa. */
function isSkinTone(r, g, b, a) {
  if (a < 16) return false;
  if (r < 90 || g < 45 || b < 20) return false;
  if (!(r > g && g >= b - 12)) return false;
  if (r - b < 22) return false;
  if (r - g > 110) return false;
  const avg = (r + g + b) / 3;
  if (avg < 55 || avg > 230) return false;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  return max - min >= 18;
}

function isOutlinePixel(r, g, b, a) {
  if (a < 16) return true;
  return r + g + b < 55;
}

const compositeCache = new Map();
const missingMasks = new Set();

/**
 * Compõe base + máscara RGBY com cores de outfit.
 * @returns {Promise<string>} data URL ou src original se não houver máscara
 */
export async function compositeOutfitSprite(baseSrc, maskSrc, colors) {
  const normalized = normalizeOutfitColors(colors);
  const key = `${baseSrc}|${maskSrc}|${outfitColorsKey(normalized)}`;
  if (compositeCache.has(key)) return compositeCache.get(key);
  if (missingMasks.has(maskSrc)) return baseSrc;

  const promise = (async () => {
    let baseImg;
    let maskImg;
    try {
      [baseImg, maskImg] = await Promise.all([loadImage(baseSrc), loadImage(maskSrc)]);
    } catch {
      missingMasks.add(maskSrc);
      return baseSrc;
    }
    const w = baseImg.naturalWidth;
    const h = baseImg.naturalHeight;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(baseImg, 0, 0);
    const baseData = ctx.getImageData(0, 0, w, h);
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = w;
    maskCanvas.height = h;
    const maskCtx = maskCanvas.getContext('2d', { willReadFrequently: true });
    maskCtx.imageSmoothingEnabled = false;
    maskCtx.drawImage(maskImg, 0, 0);
    const maskData = maskCtx.getImageData(0, 0, w, h);
    const tint = {
      head: getOutfitColor(normalized.head),
      body: getOutfitColor(normalized.body),
      legs: getOutfitColor(normalized.legs),
      feet: getOutfitColor(normalized.feet),
    };
    const out = baseData.data;
    const mask = maskData.data;
    for (let i = 0; i < out.length; i += 4) {
      if (out[i + 3] < 8) continue;
      if (isOutlinePixel(out[i], out[i + 1], out[i + 2], out[i + 3])) continue;
      if (isSkinTone(out[i], out[i + 1], out[i + 2], out[i + 3])) continue;
      const piece = maskPiece(mask[i], mask[i + 1], mask[i + 2], mask[i + 3]);
      if (!piece) continue;
      const c = tint[piece];
      out[i] = Math.round((out[i] * c.r) / 255);
      out[i + 1] = Math.round((out[i + 1] * c.g) / 255);
      out[i + 2] = Math.round((out[i + 2] * c.b) / 255);
    }
    ctx.putImageData(baseData, 0, 0);
    return canvas.toDataURL('image/png');
  })();

  compositeCache.set(key, promise);
  try {
    const url = await promise;
    compositeCache.set(key, url);
    return url;
  } catch (err) {
    compositeCache.delete(key);
    throw err;
  }
}

export function clearOutfitCompositeCache() {
  compositeCache.clear();
  trimCache.clear();
}

const trimCache = new Map();

/**
 * Recorta o sprite na caixa de pixels opacos e devolve um PNG quadrado.
 *
 * Os PNGs de entidade não têm enquadramento comum: `look-hiker-m` é um canvas
 * 64x64 com o boneco num quadrante 32x32 encostado embaixo à direita (25% de
 * preenchimento), `pokemon-collector` é 32x32 totalmente preenchido, e
 * `look-psychic-f` é 64x64 com conteúdo 48x49 solto no meio. No mundo isso não
 * aparece porque o sprite é grande e o wrapper reposiciona; num retrato de 40px
 * com object-fit:contain vira bagunça — uns bonecos minúsculos num canto, outro
 * ocupando tudo. Aqui o conteúdo é medido e recentralizado num quadrado, então
 * qualquer NPC ocupa a mesma área do retrato.
 *
 * @param {string} src caminho do PNG
 * @param {number} padding margem em porcentagem do lado do quadrado
 * @returns {Promise<string>} data URL recortada, ou o src original se falhar
 */
export async function trimmedSpriteUrl(src, padding = 0.06) {
  if (!src) return src;
  const key = `${src}|${padding}`;
  if (trimCache.has(key)) return trimCache.get(key);

  const promise = (async () => {
    let img;
    try {
      img = await loadImage(src);
    } catch {
      return src;
    }
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) return src;
    const probe = document.createElement('canvas');
    probe.width = w;
    probe.height = h;
    const pctx = probe.getContext('2d', { willReadFrequently: true });
    pctx.imageSmoothingEnabled = false;
    pctx.drawImage(img, 0, 0);
    let data;
    try {
      data = pctx.getImageData(0, 0, w, h).data;
    } catch {
      return src; // canvas contaminado (sprite de outra origem)
    }
    let minX = w;
    let minY = h;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        if (data[(y * w + x) * 4 + 3] <= 8) continue;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
    if (maxX < 0) return src; // totalmente transparente
    const cw = maxX - minX + 1;
    const ch = maxY - minY + 1;
    const side = Math.max(cw, ch);
    const pad = Math.round(side * padding);
    const size = side + pad * 2;
    const out = document.createElement('canvas');
    out.width = size;
    out.height = size;
    const octx = out.getContext('2d');
    octx.imageSmoothingEnabled = false;
    octx.drawImage(img, minX, minY, cw, ch, pad + Math.round((side - cw) / 2), pad + Math.round((side - ch) / 2), cw, ch);
    return out.toDataURL('image/png');
  })();

  trimCache.set(key, promise);
  try {
    const url = await promise;
    trimCache.set(key, url);
    return url;
  } catch {
    trimCache.delete(key);
    return src;
  }
}
